// helper functions to calculate therapist stats
import { buildEventRangeKey } from '@fullcalendar/core/internal';
import { TherapistStatsDto } from '../../../api-client';

interface GroupedData {
    [locationId: string]: TherapistStatsDto[]
}

interface TotalByLocationData {
    locationId: string, 
    totalTherapistCount: number, 
    totalValue: number,
    totalProductivity: number,
    totalAuslastung: number,
    totalRxCount: number,
    totalGaps: number,
    totalGapsInHours: number,
}

// function to calculate total stats by location
export const calculateByLocationStats = (therapistData: TherapistStatsDto[]): TotalByLocationData[] => {
    // Array to store the results
    const totalLocationData: TotalByLocationData[] = [];

    // Group by location
    const groupedData: GroupedData = Object.groupBy(therapistData, ({ locationId }) => locationId) as GroupedData;

    // for each location, calculate total stats
    Object.keys(groupedData).forEach(locationId => {
        // calculate total number of therapists
        const totalTherapistCount = groupedData[locationId].length;

        // calculate total gaps
        const totalGaps = groupedData[locationId].reduce((total, entry) => total + entry.gaps, 0);
        // convert total gaps to hours for display
        const totalGapsHours = totalGaps / 60;

        // calculate total appointment values
        const totalValue = groupedData[locationId].reduce((total, entry) => total + entry.value, 0);

        // get total Rx count
        const totalRxCount = groupedData[locationId].reduce((total, entry) => total + entry.rxCount, 0);

        // calculate total auslastung
        const totalProductivity = groupedData[locationId].reduce((total, entry) => total + entry.productivity, 0);

        const totalAuslastung = totalProductivity  / (totalProductivity + totalGaps);

        totalLocationData.push({
            locationId: locationId,
            totalTherapistCount: totalTherapistCount,
            totalValue: totalValue,
            totalProductivity: totalProductivity,
            totalAuslastung: totalAuslastung,
            totalRxCount: totalRxCount,
            totalGaps: totalGaps,
            totalGapsInHours: totalGapsHours,
        });
    });
    return totalLocationData;
}


interface TotalData {
    totalTherapistCount: number, 
    totalValue: number,
    totalAuslastung: number,
    totalRxCount: number,
    totalGaps: number,
    totalGapsInHours: number,
}

// function to calculate totals across all locations
export const calculateTotalStats = (therapistData: TherapistStatsDto[]): TotalData[] => {
    const totalData: TotalData[] = [];

    const totalTherapistCount = therapistData.length;

    // calculate total gaps
    const totalGaps = therapistData.reduce((total, entry) => total + entry.gaps, 0);
    // convert total gaps to hours for display
    const totalGapsHours = totalGaps / 60;

    // calculate total appointment values
    const totalValue = therapistData.reduce((total, entry) => total + entry.value, 0);

    // get total Rx count
    const totalRxCount = therapistData.reduce((total, entry) => total + entry.rxCount, 0);

    // calculate total auslastung
    const totalProductivity = therapistData.reduce((total, entry) => total + entry.productivity, 0);
    const totalAuslastung = totalProductivity  / (totalProductivity + totalGaps);

    totalData.push({
        totalTherapistCount: totalTherapistCount,
        totalValue: totalValue,
        totalAuslastung: totalAuslastung,
        totalRxCount: totalRxCount,
        totalGaps: totalGaps,
        totalGapsInHours: totalGapsHours,
    });
    return totalData;
}


interface TherapistTableData {
    name: string,
    // lastName: string | undefined,
    // nickName: string | undefined,
    value: number, // Umsatz
    auslastung: number,
    revenueProductivityHour: number,
    revenueScheduledHour: number,
    rxCount: number,
    gaps: number,
    gapsInHours: number,
}

interface TherapistAverageData {
    locationId: string,
    aveValue: number,
    aveAuslastung: number,
    aveRevenueProductivityHour: number,
    aveRevenueScheduledHour: number,
    aveRxCount: number,
    aveGaps: number,
    aveGapsInHours: number,
}

interface TherapistData {
    locationId: string,
    totalTherapistCount: number,
    totalValue: number,
    totalAuslastung: number,
    totalRxCount: number,
    totalGaps: number,
    totalGapsInHours: number,
    table: TherapistTableData[],
    average: TherapistAverageData[],
}

export const calculateAverageStats = (therapistData: TherapistStatsDto[]): TherapistAverageData[] => {
    const therapistAverageResults: TherapistAverageData[] = [];

    // Group by location
    const groupedData: GroupedData = Object.groupBy(therapistData, ({ locationId }) => locationId) as GroupedData;

    // for each location, calculate average stats
    Object.keys(groupedData).forEach(locationId => {
        const therapistCount = groupedData[locationId].length;

        // calculate ave appointment values
        const aveValue = groupedData[locationId].reduce((total, entry) => total + entry.value, 0) / therapistCount;

        // calculate ave auslastung
        const aveAuslaustung = groupedData[locationId].reduce((total, entry) => total + entry.auslastung, 0) / therapistCount;

        // calculate ave revenue by productivity hour
        const aveRevenueProductivityHour = groupedData[locationId].reduce((total, entry) => total + entry.revenueByProductivityHour, 0) / therapistCount;

        // calculate ave revenue by scheduled hour
        const aveRevenueScheduledHour = groupedData[locationId].reduce((total, entry) => total + entry.revenueByScheduledHour, 0) / therapistCount;

        // calculate ave Rx count
        const aveRxCount = groupedData[locationId].reduce((total, entry) => total + entry.rxCount, 0) / therapistCount;

        // calculate ave gaps
        const aveGaps = groupedData[locationId].reduce((total, entry) => total + entry.gaps, 0) / therapistCount;
        // convert ave gaps to hours for display
        const aveGapsHours = aveGaps / 60;

        therapistAverageResults.push({
            locationId: locationId,
            aveValue: aveValue,
            aveAuslastung: aveAuslaustung,
            aveRevenueProductivityHour: aveRevenueProductivityHour,
            aveRevenueScheduledHour: aveRevenueScheduledHour,
            aveRxCount: aveRxCount,
            aveGaps: aveGaps,
            aveGapsInHours: aveGapsHours,
        });
    });
    return therapistAverageResults;
}

// function to map therapist table data
export const mapTherapistTableData = (therapistData: TherapistStatsDto[], totalResults: TotalByLocationData[], aveResults: TherapistAverageData[]): TherapistData[] => {
    // array to store results
    const therapistResults: TherapistData[] = [];

    totalResults.map((row) => {
        const therapistLocation = therapistData.filter(
            (entry) => entry.locationId === row.locationId
        );

        const tableData = therapistLocation.map((entryRow) => {
            // convert gaps to hours for display
            const gapsHours = entryRow.gaps / 60;
            
            return {
                name: entryRow.user.lastName + ", " + entryRow.user.firstName,
                // lastName: entryRow.user.lastName,
                // nickName: entryRow.user.nickName,
                value: entryRow.value,
                auslastung: entryRow.auslastung,
                revenueProductivityHour: entryRow.revenueByProductivityHour,
                revenueScheduledHour: entryRow.revenueByScheduledHour,
                rxCount: entryRow.rxCount,
                gaps: entryRow.gaps,
                gapsInHours: gapsHours,
            };
        });

        const averageRow = aveResults.find(avg => avg.locationId === row.locationId);
        const aveData = averageRow ? [{
            locationId: averageRow.locationId,
            aveValue: averageRow.aveValue,
            aveAuslastung: averageRow.aveAuslastung,
            aveRevenueProductivityHour: averageRow.aveRevenueProductivityHour,
            aveRevenueScheduledHour: averageRow.aveRevenueScheduledHour,
            aveRxCount: averageRow.aveRxCount,
            aveGaps: averageRow.aveGaps,
            aveGapsInHours: averageRow.aveGapsInHours,
        }] : [];
            
        therapistResults.push({
            locationId: row.locationId,
            totalTherapistCount: row.totalTherapistCount,
            totalValue: row.totalValue,
            totalAuslastung: row.totalAuslastung,
            totalRxCount: row.totalRxCount,
            totalGaps: row.totalGaps,
            totalGapsInHours: row.totalGapsInHours,
            table: tableData,
            average: aveData,
        }); 
    });
    return therapistResults;    
};


interface FlatTherapistData {
    locationId: string,
    lastName: string | undefined,
    firstName: string | undefined,
    value: number,
    auslastung: number,
    revenueProductivityHour: number,
    revenueScheduledHour: number,
    rxCount: number,
    gaps: number,
    gapsInHours: number,
}

// function to map therapist-level data for download
export const flattenTherapistStats = (therapistStats: TherapistStatsDto[]): FlatTherapistData[] => {

    return therapistStats.map((entry) => {
        // convert gaps to hours for display
        const gapsHours = entry.gaps / 60;
        
        return {
            locationId: entry.locationId,
            lastName: entry.user.lastName,
            firstName: entry.user.firstName,
            value: entry.value,
            auslastung: entry.auslastung,
            revenueProductivityHour: entry.revenueByProductivityHour,
            revenueScheduledHour: entry.revenueByScheduledHour,
            rxCount: entry.rxCount,
            gaps: entry.gaps,
            gapsInHours: gapsHours,
        };
    });
};